import React from 'react';

import { TemplatePage } from '../../../../components/common/pager';
import { TemplateHeader } from '../../../../components/template/templateHeader';
import { TemplateText } from '../../../../components/template/templateText';
import { TemplateCheckbox } from '../../../../components/template/templateCheckbox';
import { TemplateInput } from '../../../../components/template/templateInput';
import { TemplateTextArea } from '../../../../components/template/templateTextArea';
import { TemplateToggle } from '../../../../components/template/templateToggle';
import { AnnouncementPreview } from '../components/announcementPreview';

import { paths } from '../constants/template';
import config from '../../../../constants';

interface AnnouncementsTabProps {
  onChange: (path: string, value: unknown) => void;
  /** The fields are only required while the banner is switched on. */
  announcementEnabled: boolean;
}

/**
 * System admin only tab on the event PlaceHub program. Everything it captures
 * is saved under `template.value.announcement` and shown to participating
 * charities on their program page while the banner is switched on.
 */
export const getAnnouncementsTab = ({
  onChange,
  announcementEnabled
}: AnnouncementsTabProps) => {
  const requiredWhenEnabled = { required: announcementEnabled };

  return (
    <TemplatePage
      id="announcements"
      i18nKey="programs.template.page.announcements.title"
      systemOnly
    >
      <TemplateHeader
        as="h2"
        label="programs.template.page.announcements.header"
      />

      <TemplateText
        size="small"
        label="programs.template.page.announcements.subheader"
      />

      <TemplateCheckbox
        id="announcementEnabled"
        path={paths.ANNOUNCEMENT_ENABLED}
        inputLabel="programs.template.page.announcements.checkbox"
        onChange={onChange}
      />

      <TemplateToggle path={paths.ANNOUNCEMENT_ENABLED}>
        <TemplateInput
          id="announcementTitle"
          path={paths.ANNOUNCEMENT_TITLE}
          label="programs.template.page.announcements.banner-title.label"
          subtext="programs.template.page.announcements.banner-title.subtext"
          validation={{
            ...requiredWhenEnabled,
            maxLength: true,
            length: config.PLACEHUB_ANNOUNCEMENT_TITLE_MAX_LENGTH
          }}
          onChange={onChange}
        />

        <TemplateTextArea
          id="announcementDescription"
          path={paths.ANNOUNCEMENT_DESCRIPTION}
          label="programs.template.page.announcements.description.label"
          subtext="programs.template.page.announcements.description.subtext"
          validation={{
            ...requiredWhenEnabled,
            maxLength: true,
            length: config.PLACEHUB_ANNOUNCEMENT_DESCRIPTION_MAX_LENGTH
          }}
          maxLength={config.PLACEHUB_ANNOUNCEMENT_DESCRIPTION_MAX_LENGTH}
          onChange={onChange}
        />

        <TemplateInput
          id="announcementCallToAction"
          path={paths.ANNOUNCEMENT_CALL_TO_ACTION}
          label="programs.template.page.announcements.call-to-action.label"
          subtext="programs.template.page.announcements.call-to-action.subtext"
          validation={{
            ...requiredWhenEnabled,
            maxLength: true,
            length: config.PLACEHUB_ANNOUNCEMENT_CALL_TO_ACTION_MAX_LENGTH
          }}
          onChange={onChange}
        />

        <TemplateInput
          id="announcementLink"
          path={paths.ANNOUNCEMENT_LINK}
          label="programs.template.page.announcements.link.label"
          subtext="programs.template.page.announcements.link.subtext"
          validation={{ ...requiredWhenEnabled, url: true }}
          onChange={onChange}
        />

        <TemplateHeader
          as="h3"
          label="programs.template.page.announcements.preview.header"
        />

        <AnnouncementPreview isolated />
      </TemplateToggle>
    </TemplatePage>
  );
};
